import React, { useRef, useEffect, useState } from 'react';
import { Input, Tooltip, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import { toTime, toHrsMinsSecs } from '../../libs/formatLib';
import dayjs from '../../dayjs';
import { ShiftBatRowType } from '../../model/shiftBat';
import RouteTitle from '../../components/RouteTitle';
import './GanttView.css';

const HOURS_IN_DAY = 24;
const HOUR_WIDTH = 100; // pixels per hour
const ROW_HEIGHT = 50; // pixels per row
const TIMELINE_WIDTH = HOURS_IN_DAY * HOUR_WIDTH;
const LABEL_WIDTH = 200; // width of duty code label column

const GanttView = ({ shiftBats, onSearch, sortBy, setSortBy, ganttTitle = 'Driver Duty' }) => {
  const headerContainerRef = useRef(null);
  const contentRowsRef = useRef(null);
  const [hoveredItem, setHoveredItem] = useState(null);
  
  // Handle horizontal scroll synchronization
  useEffect(() => {
    const headerContainer = headerContainerRef.current;
    const contentRows = contentRowsRef.current;
    
    if (!headerContainer || !contentRows) return;
    
    const syncScroll = (e) => {
      const target = e.target;
      
      // If scrolling happens in the content rows container
      if (contentRows.contains(target) && target.className === 'gantt-row-timeline-wrapper') {
        headerContainer.scrollLeft = target.scrollLeft;
        
        // Sync all other row timeline wrappers
        document.querySelectorAll('.gantt-row-timeline-wrapper').forEach(wrapper => {
          if (wrapper !== target) {
            wrapper.scrollLeft = target.scrollLeft;
          }
        });
      }
      
      // If scrolling happens in the header container
      if (target === headerContainer) {
        document.querySelectorAll('.gantt-row-timeline-wrapper').forEach(wrapper => {
          wrapper.scrollLeft = target.scrollLeft;
        });
      }
    };
    
    contentRows.addEventListener('scroll', syncScroll, true);
    headerContainer.addEventListener('scroll', syncScroll);
    
    return () => {
      contentRows.removeEventListener('scroll', syncScroll, true);
      headerContainer.removeEventListener('scroll', syncScroll);
    };
  }, []);

  // Generate timeline headers
  const renderTimelineHeaders = () => {
    return Array.from({ length: HOURS_IN_DAY }, (_, i) => (
      <div key={i} className="timeline-header-cell" style={{ width: HOUR_WIDTH }}>
        {String(i).padStart(2, '0')}:00
      </div>
    ));
  };

  // Convert time string to hours
  const timeToHours = (timeStr) => {
    if (!timeStr) return 0;
    
    // Handle seconds since midnight (most likely scenario)
    if (typeof timeStr === 'number') {
      // Direct conversion from seconds to hours
      return timeStr / 3600;
    }
    
    // Handle cases where we get a dayjs object
    if (typeof timeStr === 'object') {
      if (timeStr._isAMomentObject) {
        return timeStr.hour() + timeStr.minute() / 60;
      }
      // If it's a Date object
      if (timeStr instanceof Date) {
        return timeStr.getHours() + timeStr.getMinutes() / 60;
      }
      // If it's some other object with time info
      if (timeStr.hours !== undefined && timeStr.minutes !== undefined) {
        return timeStr.hours + timeStr.minutes / 60;
      }
      return 0;
    }
    
    // Handle string time format "HH:mm"
    if (typeof timeStr === 'string') {
      const [hours, minutes] = timeStr.split(':').map(Number);
      return hours + minutes / 60;
    }
    
    return 0;
  };

  // Calculate position and width for trips/breaks
  const calculateItemStyle = (startTime, endTime) => {
    const startHour = timeToHours(startTime);
    const endHour = timeToHours(endTime);
    const left = startHour * HOUR_WIDTH;
    let width = (endHour - startHour) * HOUR_WIDTH;
    
    // Handle cases where the duty crosses midnight
    if (endHour < startHour) {
      width = ((24 - startHour) + endHour) * HOUR_WIDTH;
    }
    
    return {
      left: `${left}px`,
      width: `${Math.max(width, 30)}px` // Minimum width of 30px for visibility
    };
  };

  // Handle sort
  const handleSortChange = (value) => {
    setSortBy(value);
  };

  // Format time in seconds to HH:MM
  const formatSecondsToTime = (seconds) => {
    if (typeof seconds !== 'number') return seconds;
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`;
  };

  // Get break type label
  const getBreakTypeLabel = (type) => {
    switch (type) {
      case ShiftBatRowType.break:
        return 'Break';
      case ShiftBatRowType.breakMeal:
        return 'Break Meal';
      case ShiftBatRowType.breakBroken:
        return 'Break Broken';
      default:
        return 'Unknown Break';
    }
  };

  // Get short break label
  const getShortBreakLabel = (type) => {
    switch (type) {
      case ShiftBatRowType.break:
        return 'B';
      case ShiftBatRowType.breakMeal:
        return 'BM';
      case ShiftBatRowType.breakBroken:
        return 'BB';
      default:
        return 'Unknown Break';
    }
  };

  // Detect overlapping items
  const detectOverlaps = (rows) => {
    // Filter to only include types we want to display
    const filteredRows = rows.filter(row => ![
      ShiftBatRowType.noteTimed, 
      ShiftBatRowType.note, 
      ShiftBatRowType.location, 
      ShiftBatRowType.stop, 
      ShiftBatRowType.stopNote, 
      ShiftBatRowType.template
    ].includes(row.type));
    
    // Sort rows by start time
    const sortedRows = [...filteredRows].sort((a, b) => {
      const aTime = a.getTime?.() || 0;
      const bTime = b.getTime?.() || 0;
      return aTime - bTime;
    });
    
    // Mark overlapping rows
    const overlappingRowIds = new Set();
    
    for (let i = 0; i < sortedRows.length; i++) {
      const currentRow = sortedRows[i];
      const currentStartTime = currentRow.getTime?.() || 0;
      const currentEndTime = currentRow.getEndTime?.() || 0;
      
      // Check if this row overlaps with any of the following rows
      for (let j = i + 1; j < sortedRows.length; j++) {
        const nextRow = sortedRows[j];
        const nextStartTime = nextRow.getTime?.() || 0;
        const nextEndTime = nextRow.getEndTime?.() || 0;
        
        // Check for overlap
        if (nextStartTime < currentEndTime) {
          overlappingRowIds.add(currentRow.id);
          overlappingRowIds.add(nextRow.id);
        }
      }
    }
    
    return {
      filteredRows,
      overlappingRowIds
    };
  };

  return (
    <div className="gantt-view">
      <div className="gantt-container">
        {/* Header row */}
        <div className="header-row">
          <div className="duty-code-header">{ganttTitle}</div>
          <div className="timeline-header-container" ref={headerContainerRef}>
            <div className="timeline-header">
              {renderTimelineHeaders()}
            </div>
          </div>
        </div>
        
        {/* Content rows */}
        <div className="content-rows" ref={contentRowsRef}>
          {shiftBats.map(shiftBat => {
            // Get the main duty times
            let startTime = shiftBat.getStartTime?.();
            let endTime = shiftBat.getEndTime?.();
            
            if (!startTime && !endTime) return null;
            
            const style = calculateItemStyle(startTime, endTime);
            
            const displayStartTime = formatSecondsToTime(startTime);
            const displayEndTime = formatSecondsToTime(endTime);
            
            // Detect overlaps in rows
            const { filteredRows, overlappingRowIds } = detectOverlaps(shiftBat.rows || []);
            
            return (
              <div key={shiftBat.shiftBatId} className="gantt-row">
                <div className="duty-code-cell col-lg-10">
                  <RouteTitle 
                    route={{
                      routeNumber: shiftBat.shiftBatNumber,
                      routeName: shiftBat.shiftBatName,
                      routeLogo: shiftBat.shiftBatLogo,
                      colour: shiftBat.shiftBatColour
                    }}
                    // size="sm"
                  />
                </div>
                <div className="gantt-row-timeline-wrapper">
                  <div className="gantt-row-content" style={{ width: TIMELINE_WIDTH }}>
                    <Tooltip
                      title={
                        <div>
                          <div>{shiftBat.shiftBatName || 'Duty'}</div>
                          <div>Start: {displayStartTime}</div>
                          <div>End: {displayEndTime}</div>
                          <div>Duration: {toHrsMinsSecs(shiftBat.getShiftTime?.(), false, true)}</div>
                        </div>
                      }
                    >
                      <div
                        className="gantt-item trip"
                        style={{
                          ...style, 
                          backgroundColor: shiftBat.shiftBatColour || '#1890ff'
                        }}
                      >
                        <div className="sub-items-container" style={{ 
                          display: 'flex', 
                          flexDirection: 'row', 
                          height: '100%', 
                          position: 'relative',
                          alignItems: 'center', // Ensure perfect vertical alignment
                        }}>
                          {filteredRows.map((row, index) => {
                            const rowStartTime = row.getTime?.();
                            const rowEndTime = row.getEndTime?.();
                            if (!rowStartTime || !rowEndTime) return null;
                
                            const isBreak = row.type === ShiftBatRowType.break || 
                                           row.type === ShiftBatRowType.breakMeal || 
                                           row.type === ShiftBatRowType.breakBroken;
                            const isOverlapping = overlappingRowIds.has(row.id);
                            
                            const displayRowStartTime = formatSecondsToTime(rowStartTime);
                            const displayRowEndTime = formatSecondsToTime(rowEndTime);
                            
                            // Calculate absolute position
                            const startHour = timeToHours(rowStartTime);
                            const endHour = timeToHours(rowEndTime);
                            
                            // Calculate position as percentage of parent duration
                            const parentStart = timeToHours(startTime) || 0;
                            const parentEnd = timeToHours(endTime) || 24;
                            const parentDuration = parentEnd - parentStart;
                            
                            const rowStartPercent = ((startHour - parentStart) / parentDuration) * 100;
                            const rowWidthPercent = ((endHour - startHour) / parentDuration) * 100;
                            
                            // Ensure items are visible
                            const visibleStartPercent = Math.max(0, rowStartPercent);
                            const visibleWidthPercent = Math.min(100 - visibleStartPercent, rowWidthPercent);
                            const minWidthPercent = 2;
                            const adjustedWidthPercent = Math.max(visibleWidthPercent, minWidthPercent);
                            
                            return (
                              <Tooltip
                                key={index}
                                title={
                                  <div>
                                    <div>{row.type === ShiftBatRowType.service ? 
                                      `${row.route?.routeNumber || row.route?.routeShortName || ''} - ${row.title || 'Service'}` : 
                                      (row.title || (isBreak ? getBreakTypeLabel(row.type) : 'Trip'))}
                                    </div>
                                    <div>Start: {displayRowStartTime}</div>
                                    <div>End: {displayRowEndTime}</div>
                                    <div>Duration: {toHrsMinsSecs(row.duration, false, true)}</div>
                                    {isOverlapping && <div style={{ color: 'red', fontWeight: 'bold' }}>Overlaps with another item!</div>}
                                  </div>
                                }
                              >
                                <div
                                  className={`gantt-item ${isBreak ? 'break' : 'sub-trip'} ${isOverlapping ? 'overlapping' : ''}`}
                                  style={{
                                    position: 'absolute',
                                    left: `${visibleStartPercent}%`,
                                    width: `${adjustedWidthPercent}%`,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                  }}
                                >
                                  {row.type === ShiftBatRowType.service ? (
                                    <span className="route-number-badge">
                                      {row.route?.routeNumber || row.route?.routeShortName || 'S'}
                                    </span>
                                  ) : isBreak ? (
                                    <span className="break-label">
                                      {getShortBreakLabel(row.type)}
                                    </span>
                                  ) : row.title}
                                </div>
                              </Tooltip>
                            );
                          })}
                        </div>
                      </div>
                    </Tooltip>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default GanttView; 